import { Injectable, Type, signal } from '@angular/core';

export interface ModalConfig<T = unknown> {
  /** Title shown in the dialog header */
  title?: string;
  /** Preferred dialog width; shrinks automatically on smaller screens */
  width?: string;
  /** Optional inputs to pass to the child component */
  inputs?: Partial<Record<keyof T, unknown>>;
  /** Prevent closing the dialog by clicking the overlay or pressing Escape */
  preventClose?: boolean;
  /** Called after the dialog has fully closed */
  onClose?: () => void;
}

export interface ModalRef {
  close: () => void;
}

/**
 * ModalService
 *
 * Opens arbitrary standalone components inside a centered popup dialog
 * (rendered by ModalHostComponent). Mirrors DrawerService but uses
 * `syn-dialog` instead of `syn-drawer`.
 */
@Injectable({ providedIn: 'root' })
export class ModalService {
  readonly isOpen = signal(false);
  readonly title = signal('');
  readonly width = signal<string | null>(null);
  readonly preventClose = signal(false);

  private _component = signal<Type<unknown> | null>(null);
  private _inputs = signal<Record<string, unknown>>({});
  private _onClose: (() => void) | undefined;

  readonly component = this._component.asReadonly();
  readonly inputs = this._inputs.asReadonly();

  open<T>(component: Type<T>, config: ModalConfig<T> = {}): ModalRef {
    this._component.set(component as Type<unknown>);
    this._inputs.set((config.inputs ?? {}) as Record<string, unknown>);
    this.title.set(config.title ?? '');
    this.width.set(config.width ?? null);
    this.preventClose.set(config.preventClose ?? false);
    this._onClose = config.onClose;
    this.isOpen.set(true);

    return { close: () => this.close() };
  }

  close(): void {
    this.isOpen.set(false);
    setTimeout(() => {
      this._component.set(null);
      this._inputs.set({});
      this.width.set(null);
      this._onClose?.();
      this._onClose = undefined;
    }, 300);
  }
}
